import { supabase } from "@/supabaseClient";
import {
  INVOICES_COLUMNS,
  CLIENTS_COLUMNS,
  PRODUCTS_COLUMNS,
  DRIVERS_COLUMNS,
  TRANSPORTS_COLUMNS,
} from "@/constants";
import type { Database } from "@/types/supabase";
import type { PostgrestError } from "@supabase/supabase-js";

function invoicesSelect() {
  return INVOICES_COLUMNS.join(",") + ",waybills(id)";
}

// Accumulator type for fetching all invoices
type AllInvoicesResponse = {
  data: Database["public"]["Tables"]["invoices"]["Row"][];
  error: PostgrestError | null;
};

export async function fetchTenant() {
  const result = await supabase.from("tenants").select("*").single();
  // Handle case where no tenant exists or multiple tenants exist
  if (
    result.error &&
    (result.error.code === "PGRST116" || result.error.message?.includes("JSON object requested"))
  ) {
    // Return null data instead of error to prevent app crash
    return { data: null, error: null };
  }
  return result;
}

export async function fetchInvoices() {
  const result = await supabase
    .from("invoices")
    .select(invoicesSelect())
    .order("date", { ascending: false });
  // If waybills relationship fails (table might not exist), retry without waybills
  if (
    result.error &&
    result.error.message?.includes("relationship") &&
    result.error.message?.includes("waybills")
  ) {
    const fallbackSelect = INVOICES_COLUMNS.join(",");
    return await supabase
      .from("invoices")
      .select(fallbackSelect)
      .order("date", { ascending: false });
  }
  return result;
}

export async function fetchRecentInvoices() {
  const result = await supabase
    .from("invoices")
    .select(invoicesSelect())
    .gte("date", `${new Date().getFullYear() - 1}-01-01`);
  // If waybills relationship fails (table might not exist), retry without waybills
  if (
    result.error &&
    result.error.message?.includes("relationship") &&
    result.error.message?.includes("waybills")
  ) {
    const fallbackSelect = INVOICES_COLUMNS.join(",");
    return await supabase
      .from("invoices")
      .select(fallbackSelect)
      .gte("date", `${new Date().getFullYear() - 1}-01-01`);
  }
  return result;
}

export async function fetchUser() {
  return await supabase.auth.getUser();
}

export async function fetchAllInvoices() {
  const LIMIT = 1000;
  const { count } = await supabase.from("invoices").select("*", {
    count: "exact",
    head: true,
  });
  const requetsCount = count ? Math.ceil(count / LIMIT) : 0;
  const requests = [...Array(requetsCount)].map((_, i) => {
    const rangeStart = i * LIMIT;
    const rangeEnd = rangeStart + LIMIT - 1;
    return supabase.from("invoices").select(invoicesSelect()).range(rangeStart, rangeEnd);
  });
  const responses = await Promise.all(requests);
  const data = responses.reduce<AllInvoicesResponse>(
    (acc, response) => {
      if (response.error) {
        acc.error = response.error;
      }
      if (response.data && Array.isArray(response.data)) {
        // Filter out any error objects that might be in the data array
        // Type assertion needed because Supabase types include error unions
        const items = response.data as unknown[];
        const validItems: Database["public"]["Tables"]["invoices"]["Row"][] = [];
        for (const item of items) {
          if (item === null || typeof item !== "object") continue;
          // Check if it's an error object (has 'error' property)
          if ("error" in item) continue;
          // Check if it has the required 'id' property with correct type
          if (!("id" in item)) continue;
          const itemWithId = item as { id: unknown };
          if (typeof itemWithId.id === "number") {
            validItems.push(item as Database["public"]["Tables"]["invoices"]["Row"]);
          }
        }
        acc.data.push(...validItems);
      }
      return acc;
    },
    {
      data: [],
      error: null,
    },
  );
  return data;
}

export async function fetchClients() {
  return await supabase.from("clients").select(CLIENTS_COLUMNS.join(","));
}

export async function fetchProducts() {
  return await supabase.from("products").select(PRODUCTS_COLUMNS.join(","));
}

export async function fetchInvoice(id: string) {
  return await supabase.from("invoices").select("*").eq("id", id).single();
}

export async function fetchInvoiceItems(invoiceId: string) {
  return await supabase.from("invoice_items").select("*").eq("invoice_id", invoiceId);
}

export async function fetchDrivers() {
  const result = await supabase.from("drivers").select(DRIVERS_COLUMNS.join(","));
  // Handle case where table doesn't exist (e.g., migration not applied yet)
  if (result.error) {
    const message = result.error.message ?? "";
    if (
      result.error.code === "PGRST116" ||
      result.error.code === "42P01" ||
      message.includes("does not exist")
    ) {
      return { data: [], error: null };
    }
  }
  return result;
}

export async function fetchTransports() {
  const result = await supabase.from("transports").select(TRANSPORTS_COLUMNS.join(","));
  // Handle case where table doesn't exist (e.g., migration not applied yet)
  if (result.error) {
    const message = result.error.message ?? "";
    if (
      result.error.code === "PGRST116" ||
      result.error.code === "42P01" ||
      message.includes("does not exist")
    ) {
      return { data: [], error: null };
    }
  }
  return result;
}

export async function fetchWaybill(invoiceId: string) {
  return await supabase.from("waybills").select("*").eq("invoice_id", invoiceId).single();
}

export async function createDriver(data: { name: string; license_number: string }) {
  return await supabase.from("drivers").insert(data).select().single();
}

export async function updateDriver(id: number, data: { name?: string; license_number?: string }) {
  return await supabase.from("drivers").update(data).eq("id", id).select().single();
}

export async function deleteDriver(id: number) {
  return await supabase.from("drivers").delete().eq("id", id);
}

export async function createTransport(data: { license_plate: string; model: string }) {
  return await supabase.from("transports").insert(data).select().single();
}

export async function updateTransport(
  id: number,
  data: { license_plate?: string; model?: string },
) {
  return await supabase.from("transports").update(data).eq("id", id).select().single();
}

export async function deleteTransport(id: number) {
  return await supabase.from("transports").delete().eq("id", id);
}

export async function createWaybill(data: {
  invoice_id: number;
  driver_id: number;
  transport_id: number;
  loading_date?: string;
  loading_location?: string;
  unloading_date?: string;
  unloading_location?: string;
  cargo_description?: string;
  cargo_weight?: number;
  cargo_quantity?: number;
}) {
  return await supabase.from("waybills").insert(data).select().single();
}

export async function updateWaybill(
  id: number,
  data: {
    driver_id?: number;
    transport_id?: number;
    loading_date?: string;
    loading_location?: string;
    unloading_date?: string;
    unloading_location?: string;
    cargo_description?: string;
    cargo_weight?: number;
    cargo_quantity?: number;
  },
) {
  return await supabase.from("waybills").update(data).eq("id", id).select().single();
}

export async function deleteWaybill(id: number) {
  return await supabase.from("waybills").delete().eq("id", id);
}

export async function fetchInitialData() {
  const [tenant, invoices, clients, products, drivers, transports, user] = await Promise.all([
    fetchTenant(),
    fetchRecentInvoices(),
    fetchClients(),
    fetchProducts(),
    fetchDrivers(),
    fetchTransports(),
    fetchUser(),
  ]);

  // Log errors, but skip non-critical errors for drivers/transports (table might not exist yet)
  [tenant, invoices, clients, products, user].forEach(({ error }) => {
    if (error) console.error(error.message);
  });

  // Only log drivers/transports errors if they're not "table doesn't exist" errors
  const isTableNotFoundError = (error: PostgrestError | null) => {
    if (!error) return false;
    const message = error.message ?? "";
    return (
      error.code === "PGRST116" || error.code === "42P01" || message.includes("does not exist")
    );
  };

  if (drivers.error && !isTableNotFoundError(drivers.error)) {
    console.error("Drivers fetch error:", drivers.error.message);
  }
  if (transports.error && !isTableNotFoundError(transports.error)) {
    console.error("Transports fetch error:", transports.error.message);
  }

  return {
    tenant: tenant.data,
    invoices: invoices.data,
    clients: clients.data,
    products: products.data,
    drivers: drivers.data ?? [],
    transports: transports.data ?? [],
    user: user.data.user,
  };
}
